import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {GroupBase, OptionProps} from 'react-select';
import {toast} from 'react-toastify';
import PurpleCheckmarkIcon from '../../../../../assets/icons/PurpleCheckmarkIcon.svg?react';
import {type TypedTranslation} from '../../../../i18n';
import Button from '../../../../incase-domain/components/button/Button';
import Label from '../../../../incase-domain/components/label/Label';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import {Font, IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {addFontFamilyToHead} from '../../../../utils/defaultFonts';
import Select, {Option} from '../../../components/common/Select';
import TooltipWrapper from '../../../components/common/TooltipWrapper';

interface PresentationThemeFontsProps {
    setPresentationTheme: (theme: IPresentationTheme) => void;
    presentationTheme: IPresentationTheme;
}

export default function PresentationThemeFonts(props: PresentationThemeFontsProps): JSX.Element {
    const {setPresentationTheme, presentationTheme} = props;

    const {t} = useTranslation();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [modalOpened, setModalOpened] = useState<boolean>(false);
    const [fontName, setFontName] = useState('');
    const [fontUrl, setFontUrl] = useState('');
    const [validationErrors, setValidationErrors] = useState<TypedTranslation[]>([]);

    const fontFileInput = useRef<HTMLInputElement>(null);

    const allFonts = useMemo(
        () => (presentationTheme.fonts || []).concat(presentationTheme.defaultFonts || []),
        [presentationTheme.fonts, presentationTheme.defaultFonts]
    );

    const updateFonts = (): void => {
        const errors: TypedTranslation[] = [];

        const fontNames =
            allFonts?.map((font) => {
                return font.name.toLowerCase();
            }) || [];
        fontNames.push(fontName);

        if (presentationTheme.fonts && presentationTheme.fonts?.length >= 5) {
            toast.error(t('presentationThemesPage.fonts.validation.tooManyFonts'));
            errors.push('presentationThemesPage.fonts.validation.tooManyFonts');
        } else {
            if (fontNames.some((item, index) => fontNames.indexOf(item) !== index)) {
                errors.push('presentationThemesPage.fonts.validation.duplicatedFontNames');
            }
            if (fontName === '') {
                errors.push('presentationThemesPage.fonts.validation.fontNameRequired');
            }
            if (fontUrl === '') {
                errors.push('presentationThemesPage.fonts.validation.fontUrlRequired');
            }
        }
        setValidationErrors(errors);
        if (errors.length === 0) {
            setPresentationTheme({
                ...presentationTheme,
                fonts: presentationTheme.fonts ? [...presentationTheme.fonts, {name: fontName, url: fontUrl, selected: false}] : [],
                defaultFonts: [...presentationTheme.defaultFonts],
            });
            setFontName('');
            setFontUrl('');
            setModalOpened(false);
            setValidationErrors([]);
        }
    };
    const handleCancel = (): void => {
        setModalOpened(false);
        setValidationErrors([]);
        setFontName('');
        setFontUrl('');
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (!e.target.files) return;
        const selectedFile = e.target.files[0];
        const selectedFileName = selectedFile.name.split('.');
        if (selectedFile) {
            const allowedFontTypes = [
                'font/ttf',
                'font/woff',
                'font/woff2',
                'application/font-woff',
                'application/font-ttf',
                'application/font-otf',
                'application/font-woff2',
                'application/vnd.oasis.opendocument.formula-template',
            ];
            const allowedFontExtensions = ['ttf', 'woff', 'woff2', 'otf'];
            if (
                !allowedFontTypes.includes(selectedFile.type) &&
                !allowedFontExtensions.includes(selectedFileName[selectedFileName.length - 1])
            ) {
                toast.error(t('toasts.importTypeFailed'));
            } else {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target && e.target.result !== null) {
                        const fontData = btoa(
                            new Uint8Array(e.target.result as ArrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
                        );
                        setFontName(selectedFile.name.split('.').slice(0, -1).join('.'));
                        setFontUrl(`data:${selectedFile.type};charset=utf-8;base64,${fontData}`);
                        setValidationErrors([
                            ...validationErrors.filter((error) => error !== 'presentationThemesPage.fonts.validation.fontUrlRequired'),
                        ]);
                    }
                };
                reader.readAsArrayBuffer(selectedFile);
            }
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const selectDefaultFont = (font: Font): void => {
        const updatedUploadedFonts = presentationTheme.fonts.map((fontItem) => ({
            ...fontItem,
            selected: fontItem.name === font.name ? !fontItem.selected : false,
        }));
        const updatedDefaultFonts = presentationTheme.defaultFonts.map((fontItem) => ({
            ...fontItem,
            selected: fontItem.name === font.name ? !fontItem.selected : false,
        }));

        setPresentationTheme({
            ...presentationTheme,
            fonts: updatedUploadedFonts,
            defaultFonts: updatedDefaultFonts,
        });
    };

    useEffect(() => {
        const cleanupFunctions: Array<() => void> = [];

        allFonts.forEach((font) => {
            const cleanup = addFontFamilyToHead(font);
            cleanupFunctions.push(cleanup);
        });

        return () => {
            cleanupFunctions.forEach((cleanup) => cleanup());
        };
    }, [allFonts]);

    const fontOptions: Option[] = [
        ...(allFonts?.map((font) => ({
            label: font.name,
            value: font.url,
        })) || []),
    ];

    const removeFont = (label: string): void => {
        const updatedFonts = presentationTheme.fonts.filter((font) => font.name !== label);
        setPresentationTheme({...presentationTheme, fonts: updatedFonts});
    };

    const fontNameValidationKey = validationErrors.find(
        (error) => error.includes('fontNameRequired') || error.includes('duplicatedFontNames')
    );
    const fontUrlValidationKey = validationErrors.find((error) => error.includes('fontUrlRequired'));

    const customOption = ({innerProps, label}: OptionProps<Option, boolean, GroupBase<Option>>): JSX.Element => {
        return (
            <div className="c-presentationThemes u-flex u-h-between u-p-1 u-v-center" {...innerProps}>
                <div className="c-presentationThemes__selectFont">
                    <span
                        style={{
                            fontFamily: `${allFonts.find((font) => font.name === label)?.name}`,
                        }}
                    >
                        {label}
                    </span>
                </div>
                <div className="u-flex">
                    {allFonts.find((font) => font.name === label)?.selected && (
                        <div className={`u-flex u-v-center ${allFonts.find((font) => font.name === label) && 'u-mr-1'}`}>
                            <PurpleCheckmarkIcon />
                        </div>
                    )}
                    {presentationTheme.fonts.find((font) => font.name === label) && (
                        <TooltipWrapper id="removeFont" content="Remove font">
                            <Button
                                variant="ghost"
                                size="2xs"
                                square
                                leading="ph:x-bold"
                                onClick={(e) => {
                                    e?.stopPropagation();
                                    removeFont(label);
                                }}
                            />
                        </TooltipWrapper>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="u-mt-4">
                <div className="u-flex u-v-center u-h-between u-mb-1">
                    <Label size="lg">{t('presentationThemesPage.fonts.fonts')}</Label>
                    <div className="u-flex u-gap-2">
                        <Button variant="soft" color="secondary" size="2xs" onClick={() => setModalOpened(true)}>
                            {t('presentationThemesPage.fonts.importFont')}
                        </Button>
                        <div style={{width: '14rem'}}>
                            <Select
                                value={fontOptions.find((option) => option.label === allFonts.find((font) => font.selected)?.name)}
                                onChange={(newValue) =>
                                    newValue &&
                                    selectDefaultFont({
                                        name: newValue?.label as string,
                                        url: newValue?.value as string,
                                    })
                                }
                                options={fontOptions}
                                components={{
                                    Option: (props) => customOption(props),
                                }}
                                placeholder={t('presentationThemesPage.fonts.select')}
                                menuPlacement="top"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                header={t('presEditPage.settings.themes.presentationFont')}
                description={t('presEditPage.settings.themes.importFont')}
                icon="ph:text-t"
                isOpen={modalOpened}
                onCancel={() => handleCancel()}
                onConfirm={() => updateFonts()}
                confirmText={t('common.save')}
                cancelText={t('common.cancel')}
                showCancel
                showConfirm
            >
                <div className="u-flex u-gap-2">
                    <input
                        type="text"
                        className={`e-input ${fontNameValidationKey ? 'not-valid' : ''}`}
                        placeholder={t('presentationThemesPage.fonts.fontName')}
                        maxLength={50}
                        value={fontName}
                        onChange={(e) => {
                            e.preventDefault();
                            setFontName(e.target.value);
                        }}
                    />
                    {fontNameValidationKey && <div className="is-error">{t(fontNameValidationKey)}</div>}
                    <div className="u-flex u-v-center">
                        <div className="c-importFont u-mr-2">
                            <label>
                                <input ref={fontFileInput} type="file" onChange={handleFileSelect} />
                                <Button
                                    variant="soft"
                                    size="md"
                                    color="secondary"
                                    onClick={() => {
                                        if (fontFileInput.current) {
                                            fontFileInput.current.click();
                                        }
                                    }}
                                >
                                    {t('presentationThemesPage.fonts.chooseFont')}
                                </Button>
                            </label>
                        </div>
                        {fontUrlValidationKey ? (
                            <div className="is-error">{t(fontUrlValidationKey)}</div>
                        ) : (
                            fontFileInput.current && fontFileInput.current.files && <p className="u-text-overflow">{fontUrl}</p>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
}
